import numeral from 'numeral';
import { INFERRED_TYPE_ENUM } from './chart-channels';

/**
 * A list of the general array reduction types.
 */
const ARRAY_GENERAL_REDUCTIONS = Object.freeze({
  UNWIND: 'Unwind array',
  LENGTH: 'Array length',
  INDEX: 'Array element by index',
});

/**
 * A list of the numeric array reduction types, or accumulates.
 */
const ARRAY_NUMERIC_REDUCTIONS = Object.freeze({
  MIN: 'min',
  MAX: 'max',
  MEAN: 'mean',
  SUM: 'sum',
});

/**
 * A list of the string array reduction types, or accumulates.
 */
const ARRAY_STRING_REDUCTIONS = Object.freeze({
  CONCAT: 'concat',
  MIN_LENGTH: 'min length',
  MAX_LENGTH: 'max length',
  LONGEST: 'longest',
  SHORTEST: 'shortest',
  EXISTENCE_OF_VALUE: 'existence of value',
  COUNT_OF_OCCURRENCES: 'count of occurrences',
});

/**
 * A list of all the array reduction types available.
 */
const ARRAY_REDUCTION_TYPES = Object.freeze(
  Object.assign(
    {},
    ARRAY_GENERAL_REDUCTIONS,
    ARRAY_NUMERIC_REDUCTIONS,
    ARRAY_STRING_REDUCTIONS
  )
);

/**
 * A list of transformed types based on the inferred types of  array reduction operations
 * Note: for operations that maintain the original type and the type isn't known false is returned
 *
 */
const ARRAY_REDUCTION_TRANSFORMED_TYPES = Object.freeze({
  [ARRAY_REDUCTION_TYPES.UNWIND]: false,
  [ARRAY_REDUCTION_TYPES.LENGTH]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.INDEX]: false,
  [ARRAY_REDUCTION_TYPES.MIN]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.MAX]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.MEAN]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.SUM]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.CONCAT]: INFERRED_TYPE_ENUM.STRING,
  [ARRAY_REDUCTION_TYPES.MIN_LENGTH]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.MAX_LENGTH]: INFERRED_TYPE_ENUM.NUMBER,
  [ARRAY_REDUCTION_TYPES.LONGEST]: INFERRED_TYPE_ENUM.STRING,
  [ARRAY_REDUCTION_TYPES.SHORTEST]: INFERRED_TYPE_ENUM.STRING,
  [ARRAY_REDUCTION_TYPES.EXISTENCE_OF_VALUE]: INFERRED_TYPE_ENUM.STRING,
  [ARRAY_REDUCTION_TYPES.COUNT_OF_OCCURRENCES]: INFERRED_TYPE_ENUM.NUMBER,
});

/**
 * A mechanism to configure the reduction argument fields displayed
 * to the user and the validator applied to those fields.
 *
 * Each reduction type should have an ordered array of objects, to be
 * paired up with each argument to be rendered on screen, and each object
 * has the following keys:
 *
 *  - label       the user-facing text to display
 *  - validator   a function which in the pattern of Django ReST framework
 *                returns an updated value if the value is valid,
 *                (e.g. to allow type coercion from string to integer) or
 *                throws a ValidationError if the value is not valid.
 * @see http://www.django-rest-framework.org/api-guide/validators/#writing-custom-validators
 */
const REDUCTION_ARGS_TEMPLATE = Object.freeze({
  [ARRAY_GENERAL_REDUCTIONS.INDEX]: [
    {
      label: 'Index',
      placeholder: '0-based',
      validator: value => {
        // Not sure how to warn out of bounds for this field, e.g. array
        // length is 3 but user asks for element 7. Do in a future ticket...
        if (/^-?[0-9]+$/.test(value)) {
          // Coerce to the integer value
          return parseInt(value, 10);
        }
        throw new Error(`ValidationError - Invalid value: ${value}`);
      },
    },
  ],
  [ARRAY_STRING_REDUCTIONS.EXISTENCE_OF_VALUE]: [
    {
      // https://docs.mongodb.com/manual/reference/operator/aggregation/in/#exp._S_in
      label: 'string',
      placeholder: 'case-sensitive',
      validator: value => {
        if (value.length > 0) {
          return value;
        }
        // Might want to demote this to a warning,
        // as the empty string is a potentially valid case
        throw new Error('ValidationError - A string must be provided');
      },
    },
  ],
  [ARRAY_STRING_REDUCTIONS.COUNT_OF_OCCURRENCES]: [
    {
      // https://docs.mongodb.com/manual/reference/operator/aggregation/filter/
      // https://docs.mongodb.com/manual/reference/operator/aggregation/size/
      label: 'string',
      placeholder: 'case-sensitive',
      validator: value => {
        if (value.length > 0) {
          return value;
        }
        // Might want to demote this to a warning,
        // as the empty string is a potentially valid case
        throw new Error('ValidationError - A string must be provided');
      },
    },
  ],
});

/**
 * Array reduction operators wrapped as javascript functions.
 */
const REDUCTIONS = Object.freeze({
  [ARRAY_GENERAL_REDUCTIONS.LENGTH]: function(arr) {
    return {
      $cond: { if: { $isArray: arr }, then: { $size: arr }, else: 0 },
    };
  },
  [ARRAY_GENERAL_REDUCTIONS.INDEX]: function(arr, args) {
    return {
      $arrayElemAt: [arr, args[0]],
    };
  },

  // Numeric reductions
  [ARRAY_NUMERIC_REDUCTIONS.MAX]: function(arr) {
    return {
      $max: arr,
    };
  },
  [ARRAY_NUMERIC_REDUCTIONS.MIN]: function(arr) {
    return {
      $min: arr,
    };
  },
  [ARRAY_NUMERIC_REDUCTIONS.MEAN]: function(arr) {
    return {
      $avg: arr,
    };
  },
  [ARRAY_NUMERIC_REDUCTIONS.SUM]: function(arr) {
    return {
      $sum: arr,
    };
  },

  // String reductions
  [ARRAY_STRING_REDUCTIONS.MAX_LENGTH]: function(arr) {
    return {
      $max: {
        $map: {
          input: arr,
          as: 'str',
          in: {
            $strLenCP: '$$str',
          },
        },
      },
    };
  },
  [ARRAY_STRING_REDUCTIONS.MIN_LENGTH]: function(arr) {
    return {
      $min: {
        $map: {
          input: arr,
          as: 'str',
          in: {
            $strLenCP: '$$str',
          },
        },
      },
    };
  },
  [ARRAY_STRING_REDUCTIONS.CONCAT]: function(arr) {
    return {
      $reduce: {
        input: arr,
        initialValue: '',
        in: {
          $concat: ['$$value', '$$this'],
        },
      },
    };
  },
  [ARRAY_STRING_REDUCTIONS.LONGEST]: function(arr) {
    return {
      $reduce: {
        input: arr,
        initialValue: {
          $arrayElemAt: [arr, 0],
        },
        in: {
          $cond: {
            if: {
              $gt: [{ $strLenCP: '$$this' }, { $strLenCP: '$$value' }],
            },
            then: '$$this',
            else: '$$value',
          },
        },
      },
    };
  },
  [ARRAY_STRING_REDUCTIONS.SHORTEST]: function(arr) {
    return {
      $reduce: {
        input: arr,
        initialValue: {
          $arrayElemAt: [arr, 0],
        },
        in: {
          $cond: {
            if: {
              $lt: [{ $strLenCP: '$$this' }, { $strLenCP: '$$value' }],
            },
            then: '$$this',
            else: '$$value',
          },
        },
      },
    };
  },
  [ARRAY_STRING_REDUCTIONS.EXISTENCE_OF_VALUE]: function(arr, args) {
    const expression = args[0];
    return {
      $in: [expression, arr],
    };
  },
  [ARRAY_STRING_REDUCTIONS.COUNT_OF_OCCURRENCES]: function(arr, args) {
    const expression = args[0];
    return {
      $size: {
        $filter: {
          input: arr,
          as: 'str',
          cond: { $eq: ['$$str', expression] },
        },
      },
    };
  },
});

const REDUCTION_AXIS_TITLE_FACTORIES = Object.freeze({
  [ARRAY_GENERAL_REDUCTIONS.UNWIND]: function() {
    return 'unwind ';
  },
  [ARRAY_GENERAL_REDUCTIONS.LENGTH]: function() {
    return 'length of ';
  },
  [ARRAY_GENERAL_REDUCTIONS.INDEX]: function(args) {
    return `${numeral(args[0] + 1).format('0o')} element of `;
  },
  [ARRAY_NUMERIC_REDUCTIONS.MAX]: function() {
    return 'maximum of ';
  },
  [ARRAY_NUMERIC_REDUCTIONS.MIN]: function() {
    return 'minimum of ';
  },
  [ARRAY_NUMERIC_REDUCTIONS.MEAN]: function() {
    return 'mean of ';
  },
  [ARRAY_NUMERIC_REDUCTIONS.SUM]: function() {
    return 'sum of ';
  },
  [ARRAY_STRING_REDUCTIONS.MAX_LENGTH]: function() {
    return 'length of longest string in ';
  },
  [ARRAY_STRING_REDUCTIONS.MIN_LENGTH]: function() {
    return 'length of shortest string in ';
  },
  [ARRAY_STRING_REDUCTIONS.CONCAT]: function() {
    return 'concatenation of ';
  },
  [ARRAY_STRING_REDUCTIONS.LONGEST]: function() {
    return 'longest string in ';
  },
  [ARRAY_STRING_REDUCTIONS.SHORTEST]: function() {
    return 'shortest string in ';
  },
  [ARRAY_STRING_REDUCTIONS.EXISTENCE_OF_VALUE]: function(args) {
    return `existence of string '${args[0]}' in `;
  },
  [ARRAY_STRING_REDUCTIONS.COUNT_OF_OCCURRENCES]: function(args) {
    return `count of occurrence of string '${args[0]}' in `;
  },
});

/**
 * Options used by the select component to render reduction options
 */
const ARRAY_GENERAL_REDUCTION_OPTIONS = Object.values(
  ARRAY_GENERAL_REDUCTIONS
).map(value => ({ value, label: value }));
const ARRAY_NUMERIC_REDUCTION_OPTIONS = Object.values(
  ARRAY_NUMERIC_REDUCTIONS
).map(value => ({ value, label: value }));
const ARRAY_STRING_REDUCTION_OPTIONS = Object.values(
  ARRAY_STRING_REDUCTIONS
).map(value => ({ value, label: value }));

export {
  ARRAY_GENERAL_REDUCTIONS,
  ARRAY_GENERAL_REDUCTION_OPTIONS,
  ARRAY_NUMERIC_REDUCTIONS,
  ARRAY_NUMERIC_REDUCTION_OPTIONS,
  ARRAY_STRING_REDUCTIONS,
  ARRAY_STRING_REDUCTION_OPTIONS,
  ARRAY_REDUCTION_TRANSFORMED_TYPES,
  ARRAY_REDUCTION_TYPES,
  REDUCTION_ARGS_TEMPLATE,
  REDUCTION_AXIS_TITLE_FACTORIES,
  REDUCTIONS,
};
